import {
    build_recipient_activity,
    build_recipient_timeline,
    format_status_label,
    get_status_badge_class
} from "./signing_tracker_timeline_helpers";

function normalize_text(value) {
    return String(value || "").trim();
}

function get_initials(label) {
    const words = normalize_text(label).split(/\s+/).filter(Boolean);
    if (!words.length) {
        return "?";
    }

    return words.slice(0, 2).map((word) => word.charAt(0).toUpperCase()).join("");
}

function get_recipient_status_key(recipient) {
    return normalize_text(recipient?.status || recipient?.display_status).toLowerCase();
}

function get_recipient_role(recipient, index) {
    return normalize_text(recipient?.role) || `Signer ${index + 1}`;
}

function build_recipient_identity(recipient, role, email, index) {
    const recipient_identity = {
        id: recipient?.signer_id || `${role}-${email}-${index}`,
        name: role,
        initials: get_initials(role),
        role,
        email
    };
    return recipient_identity;
}

function get_recipient_status_value(recipient, status_key) {
    return status_key || recipient?.display_status;
}

function get_recipient_total_checkpoints(total, index) {
    return total || index + 1;
}

function build_recipient_status_block(recipient, status_key, index, total) {
    const recipient_status = get_recipient_status_value(recipient, status_key);
    const total_checkpoints = get_recipient_total_checkpoints(total, index);

    const recipient_status_block = {
        status_key,
        status_label: format_status_label(recipient_status),
        status_badge_class: get_status_badge_class(recipient_status),
        current_checkpoint: Math.min(index + 1, total_checkpoints),
        total_checkpoints
    };
    return recipient_status_block;
}

function get_recipient_time_value(value) {
    return value || "";
}

function build_recipient_time_block(recipient) {
    const recipient_time_block = {
        sent_at: get_recipient_time_value(recipient?.sent_at),
        opened_at: get_recipient_time_value(recipient?.opened_at),
        completed_at: get_recipient_time_value(recipient?.completed_at),
        declined_at: get_recipient_time_value(recipient?.declined_at),
        sign_url: normalize_text(recipient?.sign_url)
    };
    return recipient_time_block;
}

function build_recipient(recipient, index, total) {
    const status_key = get_recipient_status_key(recipient);
    const role = get_recipient_role(recipient, index);
    const email = normalize_text(recipient?.email);

    const recipient_data = {
        ...build_recipient_identity(recipient, role, email, index),
        ...build_recipient_status_block(recipient, status_key, index, total),
        ...build_recipient_time_block(recipient),
        timeline: build_recipient_timeline(recipient, status_key),
        activity: build_recipient_activity(recipient)
    };
    return recipient_data;
}

function get_tracking_recipients(tracking_response) {
    const signers = Array.isArray(tracking_response?.signers) ? tracking_response.signers : [];
    return signers.map((recipient, index) => build_recipient(recipient, index, signers.length));
}

function get_tracker_document_id(route_query) {
    return normalize_text(route_query?.submissionId || route_query?.documentId);
}

function get_tracker_document_subtitle(recipients) {
    return recipients.length
        ? "Live signer progress from DocuSeal is shown below."
        : "Tracking activity will appear here once signer updates are available.";
}

function get_tracker_document_type(route_query) {
    return normalize_text(route_query?.type) || "Contract";
}

function get_tracker_document_due_date(route_query) {
    return normalize_text(route_query?.dueDate) || "--";
}

export function build_tracker_document(route_query = {}, tracking_response = null) {
    const recipients = get_tracking_recipients(tracking_response);
    const overall_status = format_status_label(tracking_response?.overall_status || route_query?.status);

    const signing_tracker = {
        id: get_tracker_document_id(route_query),
        subtitle: get_tracker_document_subtitle(recipients),
        type: get_tracker_document_type(route_query),
        due_date: get_tracker_document_due_date(route_query),
        status_label: overall_status || "Pending",
        recipients
    };
    return signing_tracker;
}
